import { inject, Injectable } from '@angular/core';

import { Apollo } from 'apollo-angular';

import { map } from 'rxjs/operators';

import { DeclareIntentData } from '@ui/shared/model';

import { RentedPropertyMatchesStore } from '@ui/tenant-pool/shared/property-matches/data-access';
import {
  checkApplicationExistQuery,
  CheckApplicationExistResult,
  createApplicationMutation,
  CreateApplicationMutationResponse,
  declareIntentMutation,
  rentedFlatsApplicationsQuery,
  RentedFlatsApplicationsQueryResult,
  revokeAllOpenApplications
} from '../queries/applications.queries';

import { PropertyMatchesFacade } from './property-matches.facade';

@Injectable({
  providedIn: 'root'
})
export class ApplicationFacade {
  private apollo = inject(Apollo);
  private propertyMatchesFacade = inject(PropertyMatchesFacade);
  private rentedStore = inject(RentedPropertyMatchesStore);

  public loadRentedFlatsApplications() {
    return this.apollo
      .query<RentedFlatsApplicationsQueryResult>({
        query: rentedFlatsApplicationsQuery,
        fetchPolicy: 'no-cache'
      })
      .pipe(map(response => response?.data?.rentedFlatsApplications));
  }

  public checkApplicationExist(propertyId: string) {
    return this.apollo
      .query<CheckApplicationExistResult>({
        query: checkApplicationExistQuery,
        variables: { propertyId },
        fetchPolicy: 'no-cache'
      })
      .pipe(map(response => response?.data?.checkApplicationExist));
  }

  public revokeAllOpenApplications() {
    return this.apollo.mutate({
      mutation: revokeAllOpenApplications,
      variables: {
        input: {
          refusalReasonType: 'DONT_SEARCH_ANYMORE'
        }
      },
      update: () => this.updateQueries()
    });
  }

  public createApplication(
    userId: string,
    propertyId: string,
    captchaResponse?: string
  ) {
    return this.apollo
      .mutate<CreateApplicationMutationResponse>({
        mutation: createApplicationMutation,
        variables: { propertyId, userId, captchaResponse },
        update: () => this.updateQueries()
      })
      .pipe(map(response => response?.data?.createApplication));
  }

  public updateQueries(rented?: boolean) {
    if (rented) {
      this.rentedStore.reload();
    } else {
      this.propertyMatchesFacade.refetchMatches();
    }
  }

  public declareIntent(declareIntentData: DeclareIntentData) {
    return this.apollo.mutate({
      mutation: declareIntentMutation,
      variables: { declareIntentData }
    });
  }
}
